// Updated Home.js
import React, { useEffect, useRef, useState } from 'react';
import styled, { createGlobalStyle, keyframes } from 'styled-components';
import PaymentSection from '../components/PaymentSection';
import Card from '../components/Card';
import VideoSection from '../components/VideoSection';
import Footer from '../components/Footer';
import ContentGrid from '../components/ContentGrid';
import { supabase } from '../supabaseClient';

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
  100% { transform: translateY(0px); }
`;

const gradientFlow = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const neonGlow = keyframes`
  0% { text-shadow: 0 0 10px #00d4ff, 0 0 20px #00d4ff; }
  50% { text-shadow: 0 0 15px #ff6f61, 0 0 25px #ff6f61; }
  100% { text-shadow: 0 0 10px #00d4ff, 0 0 20px #00d4ff; }
`;

const scanline = keyframes`
  0% { background-position: 0 0; }
  100% { background-position: 0 100vh; }
`;

const sphereFloat = keyframes`
  0% { transform: translateY(0px) rotate(0deg); }
  25% { transform: translateY(-10px) rotate(90deg); }
  50% { transform: translateY(-5px) rotate(180deg); }
  75% { transform: translateY(-15px) rotate(270deg); }
  100% { transform: translateY(0px) rotate(360deg); }
`;

const sphereGlow = keyframes`
  0% { box-shadow: 0 0 20px rgba(138, 43, 226, 0.5), 0 0 40px rgba(138, 43, 226, 0.3); }
  50% { box-shadow: 0 0 30px rgba(255, 20, 147, 0.6), 0 0 60px rgba(255, 20, 147, 0.4); }
  100% { box-shadow: 0 0 20px rgba(138, 43, 226, 0.5), 0 0 40px rgba(138, 43, 226, 0.3); }
`;

const orbitRing1 = keyframes`
  0% { transform: rotateX(60deg) rotateY(0deg); }
  100% { transform: rotateX(60deg) rotateY(360deg); }
`;

const orbitRing2 = keyframes`
  0% { transform: rotateX(-60deg) rotateY(0deg); }
  100% { transform: rotateX(-60deg) rotateY(360deg); }
`;

const orbitRing3 = keyframes`
  0% { transform: rotateX(0deg) rotateY(0deg); }
  100% { transform: rotateX(0deg) rotateY(360deg); }
`;

const GlobalStyle = createGlobalStyle`
  body {
    background: linear-gradient(135deg, #0a0a1a 0%, #121228 100%);
    font-family: 'Rajdhani', 'Inter', sans-serif;
    color: #f3f3f3;
    margin: 0;
    padding: 0;
    min-height: 100vh;
    letter-spacing: 0.01em;
    overflow-x: hidden;
  }
  
  :root {
    --primary: #00d4ff;
    --accent: #ff6f61;
    --dark: #0a0a1a;
    --gray: #1a1a3a;
    --light: #f3f3f3;
    --card-bg: rgba(34, 39, 51, 0.8);
    --card-hover: rgba(42, 49, 66, 0.9);
    --shadow: 0 8px 32px 0 rgba(0,0,0,0.36);
    --border-radius: 16px;
  }

  ::selection {
    background: var(--primary);
    color: var(--dark);
  }
`;

const ParticleBackground = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
  background: 
    linear-gradient(rgba(0, 212, 255, 0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 212, 255, 0.05) 1px, transparent 1px);
  background-size: 20px 20px;
`;

const ScanlineOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    to bottom,
    rgba(0, 212, 255, 0.03) 0%,
    rgba(0, 212, 255, 0.01) 50%,
    rgba(0, 212, 255, 0.03) 100%
  );
  background-size: 100% 6px;
  pointer-events: none;
  z-index: 0;
  animation: ${scanline} 4s linear infinite;
`;

const FloatingOrbs = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  overflow: hidden;
  z-index: -1;
  pointer-events: none;

  &::before, &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    filter: blur(60px);
    opacity: 0.4;
  }

  &::before {
    width: 400px;
    height: 400px;
    background: radial-gradient(circle, var(--primary) 0%, transparent 70%);
    top: 20%;
    left: 10%;
    animation: ${float} 8s ease-in-out infinite;
  }

  &::after {
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, var(--accent) 0%, transparent 70%);
    bottom: 15%;
    right: 10%;
    animation: ${float} 10s ease-in-out infinite reverse;
  }
`;

const FloatingSphereButton = styled.div`
  position: fixed;
  bottom: 30px;
  right: 30px;
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: linear-gradient(135deg, #7A1FD6 0%, #E60D87 50%, #3B0060 100%);
  cursor: pointer;
  z-index: 1000;
  transition: all 0.3s ease;
  animation: ${sphereFloat} 6s ease-in-out infinite, ${sphereGlow} 3s ease-in-out infinite;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  perspective: 100px;
  
  &::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    background: 
      linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.1) 50%, transparent 70%),
      linear-gradient(-45deg, transparent 30%, rgba(255, 255, 255, 0.1) 50%, transparent 70%),
      linear-gradient(135deg, transparent 30%, rgba(255, 255, 255, 0.1) 50%, transparent 70%);
    border-radius: 50%;
    animation: ${sphereFloat} 8s ease-in-out infinite reverse;
  }

  .orbit-ring {
    position: absolute;
    border-radius: 50%;
    pointer-events: none;
  }

  .orbit-ring-1 {
    width: 90px;
    height: 90px;
    border: 3px solid transparent;
    background: linear-gradient(45deg, #0066ff, #ffdd00, #0099ff, #ffaa00) border-box;
    -webkit-mask: linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask: linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0);
    mask-composite: exclude;
    animation: ${orbitRing1} 10s linear infinite;
    box-shadow: 0 0 15px rgba(0, 102, 255, 0.6);
  }

  .orbit-ring-2 {
    width: 100px;
    height: 100px;
    border: 2px solid transparent;
    background: linear-gradient(135deg, #ffdd00, #0066ff, #ffaa00, #0099ff) border-box;
    -webkit-mask: linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask: linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0);
    mask-composite: exclude;
    animation: ${orbitRing2} 15s linear infinite;
    box-shadow: 0 0 12px rgba(255, 221, 0, 0.5);
  }

  .orbit-ring-3 {
    width: 110px;
    height: 110px;
    border: 2px solid transparent;
    background: linear-gradient(90deg, #0099ff, #ffaa00, #0066ff, #ffdd00) border-box;
    -webkit-mask: linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask: linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0);
    mask-composite: exclude;
    animation: ${orbitRing3} 18.75s linear infinite;
    box-shadow: 0 0 10px rgba(0, 153, 255, 0.4);
  }

  &:hover {
    transform: scale(1.1);
    animation-play-state: paused;
    box-shadow: 0 0 40px rgba(138, 43, 226, 0.8), 0 0 80px rgba(255, 20, 147, 0.6);
    
    .orbit-ring {
      animation-play-state: paused;
      box-shadow: 0 0 25px rgba(0, 102, 255, 0.8);
    }
  }

  @media (max-width: 768px) {
    width: 60px;
    height: 60px;
    bottom: 20px;
    right: 20px;
    
    .orbit-ring-1 {
      width: 80px;
      height: 80px;
    }
    
    .orbit-ring-2 {
      width: 90px;
      height: 90px;
    }
    
    .orbit-ring-3 {
      width: 100px;
      height: 100px;
    }
  }
`;

const SphereMenu = styled.div`
  position: fixed;
  bottom: 110px;
  right: 25px;
  z-index: 1002;
  background: rgba(10, 10, 26, 0.94);
  border: 1px solid var(--primary);
  border-radius: 12px;
  padding: 16px;
  width: 240px;
  box-shadow: 0 12px 48px rgba(0, 212, 255, 0.3);
  backdrop-filter: blur(10px);
  opacity: 0;
  transform: translateY(12px) scale(0.96);
  pointer-events: none;
  transition: all 0.28s ease;

  &.visible {
    opacity: 1;
    transform: translateY(0) scale(1);
    pointer-events: auto;
  }

  .option {
    padding: 12px 16px;
    margin: 8px 0;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 500;
    text-align: center;
    transition: all 0.22s ease;
  }

  .go {
    background: linear-gradient(90deg, rgba(0,212,255,0.18), rgba(0,212,255,0.08));
    color: var(--primary);
    border: 1px solid rgba(0,212,255,0.5);
  }

  .no {
    color: var(--accent);
    border: 1px solid rgba(255,111,97,0.4);
  }

  .option:hover {
    background: rgba(0, 212, 255, 0.14);
    transform: translateX(3px);
  }

  @media (max-width: 768px) {
    right: 18px;
    width: 220px;
    bottom: 90px;
  }
`;

const TopSection = styled.section`
  background: linear-gradient(135deg, #121228 0%, #0a0a1a 100%);
  padding: 120px 0 80px 0;
  text-align: center;
  position: relative;
  overflow: hidden;
  z-index: 1;
  box-shadow: 0 10px 30px rgba(0,0,0,0.3);
  clip-path: polygon(0 0, 100% 0, 100% 95%, 0 100%);
  margin-bottom: -5%;

  h1 {
    font-size: 5rem;
    font-weight: 900;
    margin-bottom: 24px;
    background: linear-gradient(90deg, var(--primary) 0%, var(--accent) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    letter-spacing: 0.04em;
    position: relative;
    z-index: 2;
    text-shadow: 0 5px 30px rgba(0, 212, 255, 0.3);
    animation: ${neonGlow} 3s infinite alternate;
  }

  p {
    font-size: 1.5rem;
    margin-bottom: 48px;
    color: rgba(255, 255, 255, 0.9);
    font-weight: 500;
    position: relative;
    z-index: 2;
    max-width: 800px;
    margin-left: auto;
    margin-right: auto;
    line-height: 1.6;
    text-shadow: 0 2px 10px rgba(0,0,0,0.5);
  }

  @media (max-width: 767px) {
    padding: 80px 0 60px 0;
    clip-path: polygon(0 0, 100% 0, 100% 97%, 0 100%);
    
    h1 {
      font-size: 3rem;
    }
    
    p {
      font-size: 1.1rem;
      padding: 0 20px;
    }
  }
`;

const Submissions = styled.section`
  padding: 100px 5% 60px;
  position: relative;
  z-index: 2;
  background: transparent;

  h2 {
    text-align: center;
    font-size: 3.5rem;
    margin-bottom: 24px;
    color: var(--primary);
    font-weight: 800;
    letter-spacing: 0.03em;
    text-shadow: 0 5px 20px rgba(0, 212, 255, 0.3);
    position: relative;
    display: inline-block;
    left: 50%;
    transform: translateX(-50%);
    
    &::after {
      content: '';
      position: absolute;
      bottom: -10px;
      left: 0;
      width: 100%;
      height: 3px;
      background: linear-gradient(90deg, var(--primary), var(--accent));
      border-radius: 3px;
    }
  }

  p.subtitle {
    text-align: center;
    color: rgba(255, 255, 255, 0.8);
    margin-bottom: 60px;
    font-size: 1.3rem;
    font-weight: 500;
    max-width: 700px;
    margin-left: auto;
    margin-right: auto;
  }

  @media (max-width: 767px) {
    padding: 60px 5% 40px;
    
    h2 {
      font-size: 2.2rem;
    }
    
    p.subtitle {
      font-size: 1rem;
      padding: 0 20px;
    }
  }
`;

const FeaturedVideoSection = styled.section`
  padding: 100px 5%;
  background: linear-gradient(to bottom, #121228 0%, #0a0a1a 100%);
  position: relative;
  z-index: 2;
  clip-path: polygon(0 5%, 100% 0, 100% 100%, 0% 100%);
  margin-top: -5%;

  h2 {
    color: var(--accent);
    font-size: 3rem;
    font-weight: 700;
    margin-bottom: 40px;
    text-align: center;
    letter-spacing: 0.02em;
    text-shadow: 0 5px 20px rgba(255, 111, 97, 0.3);
  }

  @media (max-width: 767px) {
    padding: 60px 5%;
    clip-path: polygon(0 3%, 100% 0, 100% 100%, 0% 100%);
    
    h2 {
      font-size: 2rem;
    }
  }
`;

const GradientDivider = styled.div`
  width: 80%;
  max-width: 1000px;
  height: 4px;
  margin: 80px auto;
  background: linear-gradient(90deg, var(--primary) 0%, var(--accent) 100%);
  border-radius: 2px;
  opacity: 0.5;
  position: relative;
  z-index: 2;

  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: 0;
    width: 100%;
    height: 8px;
    background: linear-gradient(90deg, var(--primary) 0%, var(--accent) 100%);
    border-radius: 4px;
    opacity: 0.2;
    z-index: -1;
  }

  @media (max-width: 767px) {
    margin: 50px auto;
    width: 70%;
  }
`;

const AnimatedGrid = styled(ContentGrid)`
  & > * {
    transition: all 0.3s ease;
    &:hover {
      transform: translateY(-10px) scale(1.02);
      box-shadow: 0 15px 30px rgba(0, 0, 0, 0.4);
    }
  }

  /* Desktop-only: Increase photo card container height by 15% on top and bottom */
  @media (min-width: 769px) {
    & > * {
      /* Target the image container within cards */
      img, .card-image, [class*="image"] {
        padding-top: 15% !important;
        padding-bottom: 15% !important;
        object-fit: cover;
      }
      
      /* Alternative approach: add margin to image containers */
      & > div:first-child,
      & > a > div:first-child {
        padding-top: 15%;
        padding-bottom: 15%;
      }
    }
  }
`;

const TweetCardContainer = styled.div`
  background: var(--card-bg);
  border-radius: var(--border-radius);
  overflow: hidden;
  box-shadow: var(--shadow);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.3);
  }

  .tweet-wrapper {
    width: 100%;
    max-width: 600px;
    margin: 0 auto;

    a {
      display: block;
      text-decoration: none;
    }

    img {
      width: 100%;
      border-radius: 12px;
      transition: transform 0.3s ease, opacity 0.3s ease;
    }

    a:hover img {
      transform: scale(1.02);
      opacity: 0.9;
    }

    .error-message {
      color: var(--accent);
      font-size: 0.9rem;
      text-align: center;
      margin-top: 12px;
    }
  }

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const VideoCardContainer = styled.div`
  background: var(--card-bg);
  border-radius: var(--border-radius);
  overflow: hidden;
  box-shadow: var(--shadow);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.3);
  }

  .video-wrapper {
    position: relative;
    width: 100%;
    max-width: 800px;
    margin: 0 auto;

    video {
      width: 100%;
      border-radius: 12px;
      box-shadow: 0 5px 20px rgba(0,0,0,0.2);
    }

    .video-placeholder {
      width: 100%;
      height: 450px;
      background: rgba(0,0,0,0.5);
      border-radius: 12px;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      color: #fff;
      font-size: 1.2rem;
      text-align: center;
      padding: 20px;
      box-shadow: 0 5px 20px rgba(0,0,0,0.2);

      .play-icon {
        font-size: 4rem;
        margin-bottom: 20px;
      }

      .error-message {
        margin-top: 15px;
        color: var(--accent);
        font-size: 1rem;
      }
    }

    .share-overlay {
      position: absolute;
      bottom: 20px;
      right: 20px;
      z-index: 10;

      .share-button {
        background: rgba(255,255,255,0.1);
        border: none;
        border-radius: 50%;
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.3s ease;
        backdrop-filter: blur(5px);

        svg {
          width: 20px;
          height: 20px;
          fill: #fff;
        }

        &:hover {
          background: rgba(0,212,255,0.2);
          transform: scale(1.1);
        }
      }

      .share-menu {
        position: absolute;
        bottom: 50px;
        right: 0;
        background: rgba(10,10,26,0.95);
        border-radius: 12px;
        padding: 12px;
        width: 220px;
        box-shadow: 0 8px 32px rgba(0,0,0,0.3);
        backdrop-filter: blur(10px);
        opacity: 0;
        transform: translateY(10px);
        pointer-events: none;
        transition: all 0.3s ease;

        &.open {
          opacity: 1;
          transform: translateY(0);
          pointer-events: auto;
        }

        .share-option {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 16px;
          border-radius: 8px;
          cursor: pointer;
          color: #fff;
          transition: background 0.2s ease;

          &:hover {
            background: rgba(255,255,255,0.05);
          }

          .platform-icon {
            width: 24px;
            height: 24px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 50%;
            font-weight: bold;

            &.telegram { background: #0088cc; }
            &.twitter { background: #000; color: #fff; }
            &.facebook { background: #1877f2; }
            &.reddit { background: #ff4500; }
            &.copy { background: #4caf50; }
            &.embed { background: #9c27b0; }
          }
        }
      }
    }
  }

  .video-title {
    font-size: 1.4rem;
    color: #fff;
    margin: 0;
    text-align: center;
  }

  .subtitle {
    font-size: 1rem;
    color: rgba(255,255,255,0.7);
    text-align: center;
    margin: 0;
  }

  @media (max-width: 768px) {
    padding: 16px;

    .video-wrapper {
      .video-placeholder {
        height: 200px;

        .play-icon {
          font-size: 3rem;
        }
      }
    }
  }
`;

const VideoPlayer = ({ videoSrc, videoTitle }) => {
  const videoRef = useRef(null);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const handleVideoError = () => {
    setHasError(true);
    setIsLoading(false);
  };

  const handleVideoLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  const handleVideoClick = () => {
    if (hasError) {
      setHasError(false);
      setIsLoading(true);
      if (videoRef.current) {
        videoRef.current.load();
      }
    }
  };

  return (
    <div className="video-wrapper">
      <video 
        ref={videoRef}
        controls 
        preload="metadata"
        onError={handleVideoError}
        onLoadedData={handleVideoLoad}
        onCanPlay={handleVideoLoad}
        style={{ display: hasError ? 'none' : 'block' }}
      >
        <source src={videoSrc} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
      
      {(hasError || isLoading) && (
        <div className="video-placeholder" onClick={handleVideoClick}>
          <div className="play-icon">
            {isLoading ? '⏳' : '▶️'}
          </div>
          <div>{isLoading ? 'Loading video...' : videoTitle}</div>
          {hasError && (
            <div className="error-message">
              Video file not found.<br/>
              Click to retry loading.
            </div>
          )}
        </div>
      )}
      
      <VideoShare 
        videoSrc={videoSrc} 
        videoTitle={videoTitle} 
      />
    </div>
  );
};

const VideoShare = ({ videoSrc, videoTitle }) => {
  const [isShareMenuOpen, setIsShareMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsShareMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getVideoUrl = () => {
    return videoSrc;
  };

  const getEmbedCode = () => {
    const videoUrl = getVideoUrl();
    return `<video controls width="100%" style="max-width: 800px; border-radius: 12px;">
  <source src="${videoUrl}" type="video/mp4">
  Your browser does not support the video tag.
</video>
<p style="text-align: center; margin-top: 10px; font-weight: bold;">${videoTitle}</p>
<p style="text-align: center; margin: 5px 0; font-size: 0.9rem;">
  <a href="${window.location.href}" target="_blank" style="color: #00d4ff; text-decoration: none;">
    Watch more at ModMedia Network
  </a>
</p>`;
  };

  const shareOptions = [
    {
      name: 'Telegram',
      className: 'telegram',
      icon: 'TG',
      action: () => {
        const text = encodeURIComponent(`🎥 ${videoTitle}\n\n${getVideoUrl()}\n\n📰 ModMedia Network: ${window.location.href}`);
        window.open(`https://t.me/share/url?url=${encodeURIComponent(getVideoUrl())}&text=${text}`, '_blank');
      }
    },
    {
      name: 'Twitter / X',
      className: 'twitter',
      icon: '𝕏',
      action: () => {
        const text = encodeURIComponent(`🎥 ${videoTitle}\n\n${getVideoUrl()}\n\n#ModMedia #FreePress #Web3`);
        window.open(`https://twitter.com/intent/tweet?text=${text}`, '_blank');
      }
    },
    {
      name: 'Facebook',
      className: 'facebook',
      icon: 'f',
      action: () => {
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(getVideoUrl())}`, '_blank');
      }
    },
    {
      name: 'Reddit',
      className: 'reddit',
      icon: 'r/',
      action: () => {
        const title = encodeURIComponent(`🎥 ${videoTitle} - ModMedia Network`);
        window.open(`https://reddit.com/submit?url=${encodeURIComponent(getVideoUrl())}&title=${title}`, '_blank');
      }
    },
    {
      name: 'Copy Link',
      className: 'copy',
      icon: '🔗',
      action: () => {
        navigator.clipboard.writeText(getVideoUrl()).then(() => {
          alert('Video link copied to clipboard!');
        });
      }
    },
    {
      name: 'Get Embed Code',
      className: 'embed',
      icon: '</>',
      action: () => {
        navigator.clipboard.writeText(getEmbedCode()).then(() => {
          alert('Embed code copied to clipboard!');
        });
      }
    }
  ];

  return (
    <div className="share-overlay" ref={menuRef}>
      <button 
        className="share-button"
        onClick={() => setIsShareMenuOpen(!isShareMenuOpen)}
        title="Share video"
      >
        <svg viewBox="0 0 24 24">
          <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z"/>
        </svg>
      </button>
      
      <div className={`share-menu ${isShareMenuOpen ? 'open' : ''}`}>
        {shareOptions.map((option, index) => (
          <div 
            key={index}
            className="share-option"
            onClick={() => {
              option.action();
              setIsShareMenuOpen(false);
            }}
          >
            <div className={`platform-icon ${option.className}`}>
              {option.icon}
            </div>
            <span>{option.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const Home = () => {
  const [homeContent, setHomeContent] = useState([]);
  const [articles, setArticles] = useState([]);
  const [showSphere, setShowSphere] = useState(true);
  const [showSphereMenu, setShowSphereMenu] = useState(false);

  useEffect(() => {
    const hidden = localStorage.getItem('kaspasphere_promo_hidden');
    if (hidden === 'true') {
      setShowSphere(false);
    }
  }, []);

  const handleSphereClick = () => {
    setShowSphereMenu(prev => !prev);
  };

  const handleGoToSite = () => {
    window.open('https://www.kaspasphere.com/', '_blank', 'noopener,noreferrer');
    setShowSphereMenu(false);
  };

  const handleHideForever = () => {
    localStorage.setItem('kaspasphere_promo_hidden', 'true');
    setShowSphere(false);
    setShowSphereMenu(false);
  };

  useEffect(() => {
    const fetchContent = async () => {
      const { data: homeData, error: homeError } = await supabase.from('home_content').select('*');
      if (homeError) {
        console.error('Home content fetch error:', homeError);
      }
      const { data: articleData, error: articleError } = await supabase.from('articles').select('*').limit(3);
      if (articleError) {
        console.error('Articles fetch error:', articleError);
      }
      setHomeContent(homeData || []);
      setArticles(articleData || []);
    };
    fetchContent();
  }, []);

  const videoContent = homeContent.find((c) => c.type === 'video') || { title: '', file_url: '', subheader: '' };
  const imageContent = homeContent.find((c) => c.type === 'image') || { file_url: '', link_url: '' };
  const featuredVideo = homeContent.find((c) => c.type === 'featured_video') || { src: '', title: '', subheader: '', is_top: false };

  const renderFeaturedVideo = () => (
    <FeaturedVideoSection>
      <h2>Featured Video</h2>
      <VideoSection
        src={featuredVideo.src}
        title={featuredVideo.title}
      />
    </FeaturedVideoSection>
  );

  if (!showSphere) {
    return (
      <>
        <GlobalStyle />
        <ParticleBackground />
        <ScanlineOverlay />
        
        <TopSection>
          <FloatingOrbs />
          <h1>MODMEDIA</h1>
          <p>WEB DEVELOPMENT • MULTIMEDIA • GAMING</p>
          <PaymentSection />
        </TopSection>
        
        {featuredVideo.src && featuredVideo.is_top && renderFeaturedVideo()}
        
        <Submissions>
          <h2>Latest Submissions</h2>
          <p className="subtitle">Free Press Articles ⤵️ Web3, Gaming & More in Sidebar</p>
          <AnimatedGrid>
            {videoContent.file_url && (
              <VideoCardContainer>
                <VideoPlayer videoSrc={videoContent.file_url} videoTitle={videoContent.title} />
                <h3 className="video-title">{videoContent.title}</h3>
                {videoContent.subheader && <p className="subtitle">{videoContent.subheader}</p>}
              </VideoCardContainer>
            )}
            {imageContent.file_url && (
              <TweetCardContainer>
                <div className="tweet-wrapper">
                  {imageContent.link_url ? (
                    <a href={imageContent.link_url} target="_blank" rel="noopener noreferrer">
                      <img src={imageContent.file_url} alt="Bluesky post" />
                    </a>
                  ) : (
                    <img src={imageContent.file_url} alt="Bluesky post" />
                  )}
                  {!imageContent.link_url && (
                    <div className="error-message">No Bluesky link provided.</div>
                  )}
                </div>
              </TweetCardContainer>
            )}
          </AnimatedGrid>
        </Submissions>
        
        <GradientDivider />
        
        <Submissions>
          <h2>Featured Articles</h2>
          <p className="subtitle">Click an article to read the full content for free.</p>
          <AnimatedGrid>
            {articles.map((article) => {
              let imgSrc = article.img_src || '/assets/generic-crypto.jpg'; 
              return (
                <Card
                  key={article.id}
                  cardId={article.article_id}
                  imgSrc={imgSrc}
                  title={article.title}
                  previewContent={article.subheader || article.content.slice(0, 100) + '...'}
                  link={`/articles/${article.article_id}`}
                />
              );
            })}
          </AnimatedGrid>
        </Submissions>
        
        <GradientDivider />
        
        {featuredVideo.src && !featuredVideo.is_top && renderFeaturedVideo()}
        
        <Footer />
      </>
    );
  }

  return (
    <>
      <GlobalStyle />
      <ParticleBackground />
      <ScanlineOverlay />
      
      <TopSection>
        <FloatingOrbs />
        <h1>MODMEDIA</h1>
        <p>WEB3 DEVELOPMENT • HUMANE MULTIMEDIA • GAMING</p>
        <PaymentSection />
      </TopSection>
      
      {featuredVideo.src && featuredVideo.is_top && renderFeaturedVideo()}
      
      <Submissions>
        <h2>Latest Submissions</h2>
        <p className="subtitle">Free Press Articles ⤵️ Web3 & More in Sidebar</p>
        <AnimatedGrid>
          {videoContent.file_url && (
            <VideoCardContainer>
              <VideoPlayer videoSrc={videoContent.file_url} videoTitle={videoContent.title} />
              <h3 className="video-title">{videoContent.title}</h3>
              {videoContent.subheader && <p className="subtitle">{videoContent.subheader}</p>}
            </VideoCardContainer>
          )}
          {imageContent.file_url && (
            <TweetCardContainer>
              <div className="tweet-wrapper">
                {imageContent.link_url ? (
                  <a href={imageContent.link_url} target="_blank" rel="noopener noreferrer">
                    <img src={imageContent.file_url} alt="Bluesky post" />
                  </a>
                ) : (
                  <img src={imageContent.file_url} alt="Bluesky post" />
                )}
                {!imageContent.link_url && (
                  <div className="error-message">No Bluesky link provided.</div>
                )}
              </div>
            </TweetCardContainer>
          )}
        </AnimatedGrid>
      </Submissions>
      
      <GradientDivider />
      
      <Submissions>
        <h2>Featured Articles</h2>
        <p className="subtitle">Click an article to read the full content for free.</p>
        <AnimatedGrid>
          {articles.map((article) => {
            let imgSrc = article.img_src || '/assets/generic-crypto.jpg'; 
            return (
              <Card
                key={article.id}
                cardId={article.article_id}
                imgSrc={imgSrc}
                title={article.title}
                previewContent={article.subheader || article.content.slice(0, 100) + '...'}
                link={`/articles/${article.article_id}`}
              />
            );
          })}
        </AnimatedGrid>
      </Submissions>
      
      <GradientDivider />
      
      {featuredVideo.src && !featuredVideo.is_top && renderFeaturedVideo()}
      
      <FloatingSphereButton onClick={handleSphereClick}>
        <div className="orbit-ring orbit-ring-1"></div>
        <div className="orbit-ring orbit-ring-2"></div>
        <div className="orbit-ring orbit-ring-3"></div>
      </FloatingSphereButton>
      
      <SphereMenu className={showSphereMenu ? 'visible' : ''}>
        <div className="option go" onClick={handleGoToSite}>
          Go to www.kaspasphere.com
        </div>
        <div className="option no" onClick={handleHideForever}>
          No thanks – Don't show again
        </div>
      </SphereMenu>

      {showSphereMenu && (
        <div 
          onClick={() => setShowSphereMenu(false)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 999,
            background: 'transparent'
          }}
        />
      )}
      
      <Footer />
    </>
  );
};

export default Home;
